import { TSection } from "../../components/section-table";
import { TBisacEntry, TEBook, TPageTableItem } from "./types";

const center = { textAlign: "center" as const };

const formatNumber = (val: number | null | undefined): string =>
  val != null ? val.toLocaleString("de-DE") : "";

const formatCurrency = (val: number | null | undefined, symbol: string): string =>
  val != null ? `${val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}` : "";

const formatBisac = (entry: TBisacEntry): string =>
  entry?.code ?? "";

const formatBisacTooltip = (entry: TBisacEntry): string => {
  if (!entry?.heading) return "";
  return entry.subHeading
    ? `${entry.heading} / ${entry.subHeading}`
    : entry.heading;
};

const fixed = (w: string, style?: object): object => ({
  boxSizing: "border-box" as const,
  padding: "8px 4px",
  whiteSpace: "normal" as const,
  width: w,
  minWidth: w,
  maxWidth: w,
  ...style,
});

const flex = (style?: object): object => ({
  boxSizing: "border-box" as const,
  padding: "8px 4px",
  whiteSpace: "normal" as const,
  overflow: "hidden",
  ...style,
});

export const getStaticColumns = (
  t: (key: string) => string,
  selectEbook: (ebook: TEBook) => void,
  onCoverClick?: (ebook: TEBook) => void,
): TPageTableItem<TEBook>[] => [
    {
      label: t("cover"),
      key: "cover",
      roles: [],
      type: "image",
      onClick: onCoverClick,
      style: fixed("80px"),
    },
    {
      label: t("isbn"),
      key: "isbn",
      roles: [],
      sortable: true,
      onClick: selectEbook,
      style: fixed("140px"),
    },
    {
      label: t("title"),
      key: "title",
      roles: [],
      sortable: true,
      tooltip: true,
      style: fixed("250px", { overflow: "hidden", textOverflow: "ellipsis" }),
    },
    {
      label: t("author"),
      key: "author",
      roles: [],
      style: fixed("140px"),
    },
    {
      label: t("publishingDate"),
      key: "publishingDate",
      roles: [],
      type: "date",
      sortable: true,
      style: fixed("120px", { ...center }),
    },
    {
      label: t("advertisedBook"),
      key: "advertisedBook",
      roles: ["EbookPublisherMarketingInfo"],
      sortable: true,
      sortKey: "advertisementLink",
      tooltip: true,
      style: fixed("200px", { overflow: "hidden", textOverflow: "ellipsis" }),
    },
  ];

export const BISAC_FIELDS: { id: string; field: keyof TEBook["bisacs"] }[] = [
  { id: "bisacMain", field: "ebookBisacMain" },
  { id: "bisacSub1", field: "ebookBisacSubOne" },
  { id: "bisacSub2", field: "ebookBisacSubTwo" },
  { id: "bisacSub3", field: "ebookBisacSubTree" },
  { id: "bisacSub4", field: "ebookBisacSubFour" },
  { id: "bisacSub5", field: "ebookBisacSubFive" },
];

export const KEYWORD_FIELDS: { id: string; field: string }[] =
  Array.from({ length: 7 }, (_, i) => ({ id: `keyword${i + 1}`, field: `keyword${i + 1}` }));

export const getSections = (
  t: (key: string) => string,
  onBisacClick?: (ebook: TEBook, bisacField: string) => void,
  onKeywordClick?: (ebook: TEBook, keywordField: string) => void,
  onFilesClick?: (ebook: TEBook) => void,
): TSection<TEBook>[] => [
    {
      identifier: "links",
      label: t("links"),
      columns: [
        {
          id: "link-thalia",
          label: t("thalia"),
          key: "links",
          roles: [],
          type: "state",
          sortable: true,
          sortKey: "thaliaLinkOk",
          link: (item: TEBook) => item.links?.thalia ?? "",
          valueTransform: ((_: any, item: TEBook) => (item.links?.thalia ? "y" : "n")) as any,
          style: flex({ ...center }),
        },
        {
          id: "link-amazon-de",
          label: t("amazon-de"),
          key: "links",
          roles: ["EbookLinkCheckAmazonDEGrp"],
          type: "state",
          sortable: true,
          sortKey: "amazonLinkOk",
          link: (item: TEBook) => item.links?.amazonDe ?? "",
          valueTransform: ((_: any, item: TEBook) => (item.links?.amazonDe ? "y" : "n")) as any,
          style: flex({ ...center }),
        },
        {
          id: "link-amazon-com",
          label: t("amazon-com"),
          key: "links",
          roles: ["EbookLinkCheckAmazonUSGrp"],
          type: "state",
          sortable: true,
          sortKey: "amazonLinkOk",
          link: (item: TEBook) => item.links?.amazonCom ?? "",
          valueTransform: ((_: any, item: TEBook) => (item.links?.amazonCom ? "y" : "n")) as any,
          style: flex({ ...center }),
        },
        {
          id: "link-amazon-co-uk",
          label: t("amazon-co-uk"),
          key: "links",
          roles: ["EbookLinkCheckAmazonUKGrp"],
          type: "state",
          sortable: true,
          sortKey: "amazonLinkOk",
          link: (item: TEBook) => item.links?.amazonCoUk ?? "",
          valueTransform: ((_: any, item: TEBook) => (item.links?.amazonCoUk ? "y" : "n")) as any,
          style: flex({ ...center }),
        },
        {
          id: "link-apple",
          label: t("apple"),
          key: "links",
          roles: [],
          type: "state",
          sortable: true,
          sortKey: "appleLinkOk",
          link: (item: TEBook) => item.links?.apple ?? "",
          valueTransform: ((_: any, item: TEBook) => (item.links?.apple ? "y" : "n")) as any,
          style: flex({ ...center }),
        },
        {
          id: "link-kobo",
          label: t("kobo"),
          key: "links",
          roles: ["EbookLinkCheckKoboGrp"],
          type: "state",
          sortable: true,
          sortKey: "koboLinkOk",
          link: (item: TEBook) => item.links?.kobo ?? "",
          valueTransform: ((_: any, item: TEBook) => (item.links?.kobo ? "y" : "n")) as any,
          style: flex({ ...center }),
        },
        {
          id: "link-google",
          label: t("google"),
          key: "links",
          roles: ["EbookLinkCheckGoogleGrp"],
          type: "state",
          sortable: true,
          sortKey: "googleLinkOk",
          link: (item: TEBook) => item.links?.google ?? "",
          valueTransform: ((_: any, item: TEBook) => (item.links?.google ? "y" : "n")) as any,
          style: flex({ ...center }),
        },
        {
          id: "link-barnes",
          label: t("barnes"),
          key: "links",
          roles: ["EbookLinkCheckBNGrp"],
          type: "state",
          sortable: true,
          sortKey: "bunLinkOk",
          link: (item: TEBook) => item.links?.barnes ?? "",
          valueTransform: ((_: any, item: TEBook) => (item.links?.barnes ? "y" : "n")) as any,
          style: flex({ ...center }),
        },
      ],
    },
    {
      identifier: "prices",
      label: t("prices"),
      columns: [
        {
          id: "amountLastPeriod",
          label: t("amountLastPeriod"),
          key: "prices",
          roles: ["EbookAuthorSaleInfo", "EbookPublisherMarketingInfo"],
          valueTransform: ((_: any, item: TEBook) => formatNumber(item.prices?.amountLastPeriod)) as any,
          style: flex({ ...center, fontVariantNumeric: "tabular-nums" }),
        },
        {
          id: "amountFirst1Month",
          label: t("amountFirst1Month"),
          key: "prices",
          roles: ["EbookAuthorSaleInfo", "EbookPublisherMarketingInfo"],
          sortable: true,
          sortKey: "amountFirst1month",
          valueTransform: ((_: any, item: TEBook) => formatNumber(item.prices?.amountFirst1Month)) as any,
          style: flex({ ...center, fontVariantNumeric: "tabular-nums" }),
        },
        {
          id: "amountFirst6Months",
          label: t("amountFirst6Months"),
          key: "prices",
          roles: ["EbookAuthorSaleInfo", "EbookPublisherMarketingInfo"],
          sortable: true,
          sortKey: "amountFirst6months",
          valueTransform: ((_: any, item: TEBook) => formatNumber(item.prices?.amountFirst6Months)) as any,
          style: flex({ ...center, fontVariantNumeric: "tabular-nums" }),
        },
        {
          id: "amountFirst12Months",
          label: t("amountFirst12Months"),
          key: "prices",
          roles: ["EbookAuthorSaleInfo", "EbookPublisherMarketingInfo"],
          sortable: true,
          sortKey: "amountFirst12months",
          valueTransform: ((_: any, item: TEBook) => formatNumber(item.prices?.amountFirst12Months)) as any,
          style: flex({ ...center, fontVariantNumeric: "tabular-nums" }),
        },
        {
          id: "amountTotal",
          label: t("amountTotal"),
          key: "prices",
          roles: ["EbookAuthorSaleInfo", "EbookPublisherMarketingInfo"],
          valueTransform: ((_: any, item: TEBook) => formatNumber(item.prices?.amountTotal)) as any,
          style: flex({ ...center, fontWeight: "600", fontVariantNumeric: "tabular-nums" }),
        },
        {
          id: "priceEur",
          label: `${t("price")} ${t("eur")}`,
          key: "prices",
          roles: [],
          sortable: true,
          sortKey: "priceEUR",
          valueTransform: ((_: any, item: TEBook) => formatCurrency(item.prices?.priceEur, "€")) as any,
          style: flex({ ...center, fontVariantNumeric: "tabular-nums" }),
        },
        {
          id: "priceChf",
          label: `${t("price")} ${t("chf")}`,
          key: "prices",
          roles: [],
          sortable: true,
          sortKey: "priceCHF",
          valueTransform: ((_: any, item: TEBook) => formatCurrency(item.prices?.priceChf, "CHF")) as any,
          style: flex({ ...center, fontVariantNumeric: "tabular-nums" }),
        },
        {
          id: "priceUsd",
          label: `${t("price")} ${t("usd")}`,
          key: "prices",
          roles: [],
          sortable: true,
          sortKey: "priceUSD",
          valueTransform: ((_: any, item: TEBook) => formatCurrency(item.prices?.priceUsd, "$")) as any,
          style: flex({ ...center, fontVariantNumeric: "tabular-nums" }),
        },
        {
          id: "priceIntervalEur",
          label: `${t("priceInterval")} ${t("eur")}`,
          key: "prices",
          roles: ["EbookPublisherMarketingInfo"],
          valueTransform: ((_: any, item: TEBook) => item.prices?.priceIntervalEur ?? "") as any,
          style: flex({ ...center, fontVariantNumeric: "tabular-nums" }),
        },
      ],
    },
    {
      identifier: "bisacs",
      label: t("bisacs"),
      roles: ['MetadataUpdate'],
      columns: BISAC_FIELDS.map(({ id, field }) => ({
        id,
        label: t(id),
        key: "bisacs",
        roles: [],
        onClick: onBisacClick
          ? (item: TEBook) => onBisacClick(item, field)
          : undefined,
        valueTransform: ((_: any, item: TEBook) => formatBisac(item.bisacs?.[field])) as any,
        tooltipTransform: ((_: any, item: TEBook) => formatBisacTooltip(item.bisacs?.[field])) as any,
        style: flex({ ...center }),
      })),
    },
    {
      identifier: "keywords",
      label: t("keywords"),
      roles: ['MetadataUpdate'],
      columns: KEYWORD_FIELDS.map(({ id, field }) => ({
        id,
        label: `${t("keyword")} ${field.replace("keyword", "")}`,
        key: "amazonKeywords",
        roles: [] as string[],
        onClick: onKeywordClick
          ? (item: TEBook) => onKeywordClick(item, field)
          : undefined,
        valueTransform: ((_: any, item: TEBook) =>
          item.amazonKeywords?.[field as keyof TEBook["amazonKeywords"]] || ""
        ) as any,
        tooltipTransform: ((_: any, item: TEBook) =>
          item.amazonKeywords?.[field as keyof TEBook["amazonKeywords"]] || ""
        ) as any,
        lineClamp: 3,
        style: flex(),
      })),
    },
    {
      identifier: "files",
      label: t("files"),
      roles: ['EbookCollaborationGrp'],
      columns: [
        {
          id: "filesList",
          label: t("files"),
          key: "isbn",
          roles: [],
          onClick: onFilesClick,
          valueTransform: ((_: any) => t("files")) as any,
          style: flex({ ...center }),
        },
        {
          id: "epub",
          label: t("epub"),
          key: "files",
          roles: [],
          type: "state",
          sortable: true,
          sortKey: "hasEpub",
          link: (item: TEBook) => `/api/ebook/${item.isbn}?type=EPUB`,
          valueTransform: ((_: any, item: TEBook) => (item.files?.epub ? "y" : "n")) as any,
          style: flex({ ...center }),
        },
      ],
    },
  ];
